import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Play,
  Copy,
  Rocket,
  Plus,
  Lightbulb,
  MessageSquare,
  ExternalLink,
  Globe,
  Trash2,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

/* ────────────────────────────────────────────────────
   Lavender Dream Design System
   ──────────────────────────────────────────────────── */
const GRADIENT_BG = `
  radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
  radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
  radial-gradient(ellipse 55% 45% at 50% 105%, rgba(155, 195, 245, 0.35) 0%, transparent 55%),
  radial-gradient(ellipse 45% 35% at 85% 55%, rgba(245, 158, 182, 0.28) 0%, transparent 48%),
  #ede9f5
`;

interface Project {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  idea_id: string | null;
  status: string;
  project_status: 'idea' | 'building' | 'live';
  isLive: boolean;
  isFailed: boolean;
  origin: 'idea' | 'prompt';
  previewUrl: string | null;
  customDomain: string | null;
}

export const MyAppsHub = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data: projects, isLoading } = useQuery({
    queryKey: ['my-apps', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: projectsData, error: projectsError } = await supabase
        .from('code_projects')
        .select('id, name, description, idea_id, project_status, status, created_at, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (projectsError) throw projectsError;

      const projectIds = projectsData?.map(p => p.id) || [];

      // Fetch latest deployment per project (ordered by most recent)
      // NOTE: deployment_history table may not exist - silently fail with empty array
      let deployments: any[] = [];
      try {
        const { data, error } = await (supabase as any)
          .from('deployment_history')
          .select('project_id, status, preview_url, created_at')
          .in('project_id', projectIds)
          .order('created_at', { ascending: false });
        if (!error && data) deployments = data;
      } catch {
        // Table doesn't exist - continue with empty deployments
      }

      // Build map: project_id → latest deployment info
      const deployMap = new Map<string, { status: string; previewUrl: string | null }>();
      for (const d of (deployments || [])) {
        if (!deployMap.has(d.project_id)) {
          deployMap.set(d.project_id, { status: d.status, previewUrl: d.preview_url });
        }
      }

      return (projectsData || []).map(project => {
        const hasIdeaId = !!(project as any).idea_id;
        const isFromIdea = hasIdeaId ||
          project.description?.toLowerCase().includes('startup') ||
          project.description?.toLowerCase().includes('idea');

        const latestDeploy = deployMap.get(project.id);
        const isLive = latestDeploy?.status === 'success';
        const isFailed = latestDeploy?.status === 'error' || latestDeploy?.status === 'failed';
        const projectStatus = isLive ? 'live' : ((project as any).project_status || 'idea');

        let customDomain: string | null = null;
        try { customDomain = localStorage.getItem(`dayone:customDomain:${project.id}`); }
        catch { /* ignore */ }

        return {
          ...project,
          idea_id: (project as any).idea_id || null,
          status: (project as any).status || 'draft',
          project_status: projectStatus as 'idea' | 'building' | 'live',
          isLive,
          isFailed,
          origin: isFromIdea ? 'idea' as const : 'prompt' as const,
          previewUrl: latestDeploy?.previewUrl || null,
          customDomain,
        };
      }) as Project[];
    },
    enabled: !!user?.id,
  });

  const duplicateMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const project = projects?.find(p => p.id === projectId);
      if (!project || !user?.id) throw new Error('Project not found');

      const { data, error } = await supabase
        .from('code_projects')
        .insert({
          name: `${project.name} (Copy)`,
          description: project.description,
          user_id: user.id,
        })
        .select('id, name, description, created_at, updated_at')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-apps'] });
      toast.success('Project duplicated');
    },
    onError: () => {
      toast.error('Failed to duplicate project');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (projectId: string) => {
      // Delete project files from storage
      const { data: files } = await supabase.storage
        .from('project-files')
        .list(projectId);
      if (files && files.length > 0) {
        await supabase.storage
          .from('project-files')
          .remove(files.map(f => `${projectId}/${f.name}`));
      }
      // Delete the project record
      const { error } = await supabase
        .from('code_projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-apps'] });
      toast.success('App deleted');
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error('Failed to delete app');
      setDeleteTarget(null);
    },
  });

  const handleOpen = (projectId: string) => {
    navigate(`/code-workspace?projectId=${projectId}`);
  };

  const handleDeploy = (projectId: string) => {
    navigate(`/code-workspace?projectId=${projectId}&action=deploy`);
  };

  const handleCreateNew = () => {
    navigate('/create');
  };

  const handleViewIdea = (ideaId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/idea/${ideaId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ background: GRADIENT_BG }}>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Skeleton className="h-8 w-32" style={{ background: "rgba(255,255,255,0.5)" }} />
            <Skeleton className="h-10 w-28" style={{ background: "rgba(255,255,255,0.5)" }} />
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-2xl" style={{ background: "rgba(255,255,255,0.5)" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: GRADIENT_BG, fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1
            className="text-[26px] font-bold"
            style={{ fontFamily: "'Instrument Serif', serif", color: "#18151f" }}
          >
            My Apps
          </h1>
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-5 py-2 text-[14px] font-medium text-white rounded-full transition-all hover:opacity-90"
            style={{ background: "#18151f" }}
          >
            <Plus className="h-4 w-4" />
            New App
          </button>
        </div>

        {/* Empty State */}
        {(!projects || projects.length === 0) && (
          <div className="flex flex-col items-center justify-center py-24">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8"
              style={{
                background: 'rgba(124, 106, 240, 0.1)',
                border: '1px solid rgba(124, 106, 240, 0.2)',
              }}
            >
              <Rocket className="w-9 h-9" style={{ color: "#7c6af0" }} />
            </div>
            <h2
              className="text-2xl font-bold mb-3 text-center"
              style={{ fontFamily: "'Instrument Serif', serif", color: "#18151f" }}
            >
              Your first app is one prompt away
            </h2>
            <p className="text-center max-w-md mb-8 leading-relaxed" style={{ color: "#6b5f8a" }}>
              Describe what you want to build and watch it come to life in minutes.
            </p>
            <button
              onClick={() => navigate('/create')}
              className="flex items-center gap-2 px-8 py-3 text-[15px] font-medium text-white rounded-full transition-all hover:opacity-90"
              style={{ background: "#18151f" }}
            >
              Start Building
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Project List */}
        {projects && projects.length > 0 && (
          <div className="space-y-3">
            {projects.map((project) => {
              const timeAgo = formatDistanceToNow(new Date(project.updated_at), { addSuffix: true });

              const getStatusBadge = () => {
                if (project.isFailed) return { label: 'Failed', bg: 'rgba(239,68,68,0.1)', color: '#ef4444' };
                if (project.isLive) return { label: 'Live', bg: 'rgba(16,185,129,0.1)', color: '#059669' };
                if (project.project_status === 'building') return { label: 'Building', bg: 'rgba(59,130,246,0.1)', color: '#3b82f6' };
                return { label: 'Draft', bg: 'rgba(124,106,240,0.1)', color: '#7c6af0' };
              };
              const badge = getStatusBadge();

              return (
                <div
                  key={project.id}
                  className="group flex items-center gap-4 rounded-2xl transition-all cursor-pointer"
                  style={{
                    background: "rgba(255, 255, 255, 0.72)",
                    border: "1px solid rgba(255, 255, 255, 0.85)",
                    padding: "16px 20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.72)";
                  }}
                  onClick={() => handleOpen(project.id)}
                >
                  {/* App Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3
                        className="font-semibold truncate"
                        style={{ fontSize: "15px", color: "#18151f" }}
                      >
                        {project.name}
                      </h3>
                      <span
                        className="flex-shrink-0 text-[11px] font-medium"
                        style={{
                          background: badge.bg,
                          color: badge.color,
                          borderRadius: "20px",
                          padding: "2px 10px",
                        }}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-[13px]" style={{ color: "#6b5f8a" }}>
                      <span className="flex items-center gap-1.5">
                        {project.origin === 'idea' ? (
                          <Lightbulb className="h-3.5 w-3.5" />
                        ) : (
                          <MessageSquare className="h-3.5 w-3.5" />
                        )}
                        {project.origin === 'idea' ? 'From idea' : 'From prompt'}
                      </span>
                      <span className="flex items-center gap-1.5 text-[12px]">
                        <Clock className="h-3.5 w-3.5" />
                        {timeAgo}
                      </span>
                      {project.idea_id && (
                        <button
                          onClick={(e) => handleViewIdea(project.idea_id!, e)}
                          className="flex items-center gap-1 hover:underline"
                          style={{ color: "#7c6af0" }}
                        >
                          <ExternalLink className="h-3 w-3" />
                          View Research
                        </button>
                      )}
                    </div>
                    {project.isLive && project.previewUrl && (
                      <a
                        href={project.customDomain ? `https://${project.customDomain}` : project.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs hover:underline mt-1"
                        style={{ color: "#7c6af0" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Globe className="h-3 w-3" />
                        {project.customDomain || (() => { try { return new URL(project.previewUrl!).hostname; } catch { return project.previewUrl; } })()}
                      </a>
                    )}
                  </div>

                  {/* Actions */}
                  <div
                    className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleOpen(project.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-lg transition-colors"
                      style={{ color: "#6b5f8a" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#18151f"; e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#6b5f8a"; e.currentTarget.style.background = "transparent"; }}
                    >
                      <Play className="h-3.5 w-3.5" />
                      Open
                    </button>
                    {project.previewUrl && (
                      <button
                        onClick={() => window.open(project.previewUrl!, '_blank')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-lg transition-colors"
                        style={{ color: "#6b5f8a" }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#18151f"; e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#6b5f8a"; e.currentTarget.style.background = "transparent"; }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View
                      </button>
                    )}
                    <button
                      onClick={() => handleDeploy(project.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-lg transition-colors"
                      style={{ color: "#6b5f8a" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#18151f"; e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#6b5f8a"; e.currentTarget.style.background = "transparent"; }}
                    >
                      <Rocket className="h-3.5 w-3.5" />
                      {project.isLive ? 'Redeploy' : 'Deploy'}
                    </button>
                    <button
                      onClick={() => duplicateMutation.mutate(project.id)}
                      disabled={duplicateMutation.isPending}
                      className="p-2 rounded-lg transition-colors disabled:opacity-50"
                      style={{ color: "#6b5f8a" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#18151f"; e.currentTarget.style.background = "rgba(0,0,0,0.05)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#6b5f8a"; e.currentTarget.style.background = "transparent"; }}
                      title="Duplicate"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ id: project.id, name: project.name })}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: "#6b5f8a" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "rgba(239,68,68,0.1)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#6b5f8a"; e.currentTarget.style.background = "transparent"; }}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This cannot be undone. The app and all its files will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
